import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Content } from 'rsuite';
import { LoadingMenu } from '../../../../../../components/LoadingSpinner';
import ModalConfirmation from '../../../../../../components/ModalConfirmation';
import SelectPill from '../../../../../../components/SelectPill';

import { ChainSelect } from '../ChainSelect';
import { FilterPreference } from '../FilterPreference';
import { LocalSelect } from '../LocalSelect';
import { ModalSaveFilters } from '../ModalSaveFilters';
import { SaveFilters } from '../SaveFilters';

import { FILTER_LABEL_LIST, LOCALE, WEEK_PERIOD_LIST } from '../../constants';
import { ProductSelect } from '../ProductSelect';
import { useOmnichannelStore } from '../../store/ModuloOmnichannel.store';
import { FilterType } from '../../types';
import styles from './FiltersArea.module.scss';
import { useOmnichannelPreferenceFilter } from '../../hooks/useOmnichannelPreferenceFilter';
import { useOmnichannelSaveFilters } from '../../hooks/useOmnichannelSaveFilters';
import { useDeleteOmnichannelSavedFilter } from '../../hooks/useDeleteOmnichannelSavedFilter';

const changePlaceholder = (name: FilterType, list: string[]) => {
  const hasList = list?.length > 0;
  const placeholder = {
    rede: !hasList ? 'Escolha até 10 redes' : 'Redes selecionadas',
    cidade: !hasList ? 'Escolha as cidades' : 'Cidades selecionadas',
    uf: !hasList ? 'Escolha as UFs' : 'UFs selecionadas',
  };
  return placeholder[name] ?? 'Selecione uma opção';
};

const renderMenu = (menu: React.ReactNode) => (
  <span>
    <LoadingMenu area="omnichannel-filter" />
    {menu}
  </span>
);

export const FiltersArea = () => {
  const omnichannelPreferences = useOmnichannelStore(state => state.omnichannelPreferences);
  const showModalOmnichannelSaveFilters = useOmnichannelStore(state => state.showModalOmnichannelSaveFilters);
  const showModalOmnichannelDeleteFilters = useOmnichannelStore(state => state.showModalOmnichannelDeleteFilters);
  const selectedSavedFilter = useOmnichannelStore(state => state.selectedSavedFilter);
  const setOmnichannelState = useOmnichannelStore(state => state.setOmnichannelState);
  const setOmnichannelMutateRequestBody = useOmnichannelStore(state => state.setOmnichannelMutateRequestBody);

  const { handleOmnichannelSaveFilters, handleChangeNameSavedFilter } = useOmnichannelSaveFilters();
  const { preferenceSaveLoading, handleChangePriceType } = useOmnichannelPreferenceFilter();
  const { deleteOmnichannelSavedFilter, isDeleting } = useDeleteOmnichannelSavedFilter();

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const handleSelectWeekPeriod = (value: number) => {
    const semanas = Number(value);
    setOmnichannelMutateRequestBody('semanas', semanas);
  };

  const handleHideModalSavedFilters = () => {
    setOmnichannelState('showModalOmnichannelSaveFilters', false);
    setOmnichannelState('nameSavedFilter', '');
  };

  return (
    <Content className={styles['filters-area-content']}>
      <div className={styles['inner-div']}>
        <div className={styles['title-container']}>
          {FILTER_LABEL_LIST.map(title => (
            <div key={title} className={styles.title}>
              <p>{title}</p>
            </div>
          ))}
        </div>
        <div>
          <div className={styles['filter-selection-wrapper']}>
            <ProductSelect locale={LOCALE} />
          </div>
          <div className={styles['filter-selection-wrapper']}>
            <ChainSelect renderMenu={renderMenu} locale={LOCALE} placeholder={changePlaceholder} />
          </div>
          <div className={styles['filter-selection-wrapper']}>
            <LocalSelect renderMenu={renderMenu} locale={LOCALE} placeholder={changePlaceholder} />
          </div>
          <div className={styles['filter-selection-wrapper']}>
            <Controller
              name="periodoSemanas"
              control={control}
              render={({ field }) => (
                <SelectPill
                  id={errors?.periodoSemanas ? 'select-pill-is-empty' : 'select-picker-pill'}
                  placeholder="Escolha uma opção"
                  name="periodoSemanas"
                  cleanable={false}
                  value={field.value}
                  data={WEEK_PERIOD_LIST}
                  locale={LOCALE}
                  onSelect={(value: number) => {
                    field.onChange(value);
                    handleSelectWeekPeriod(value);
                  }}
                />
              )}
            />
          </div>
        </div>
        <div className={styles['preference-wrapper']}>
          <SaveFilters renderMenu={renderMenu} onClickDeleteSavedFilter={() => setOmnichannelState('showModalOmnichannelDeleteFilters', true)} />
          <FilterPreference
            className={styles['preference-dropdown']}
            loading={preferenceSaveLoading}
            tipoPrecoValue={omnichannelPreferences.tipoPreco}
            onChangePriceType={handleChangePriceType}
          />
        </div>
      </div>
      <ModalConfirmation
        title="Excluir filtro"
        loading={isDeleting}
        message={
          selectedSavedFilter?.nomeFiltroSalvo
            ? `Deseja deletar o filtro ${selectedSavedFilter.nomeFiltroSalvo}?
                    Esta ação não poderá ser desfeita`
            : null
        }
        show={showModalOmnichannelDeleteFilters}
        buttonConfirm="Deletar filtro"
        buttonCancel="Cancelar"
        onConfirm={() => {
          if (selectedSavedFilter?.idFiltroSalvo) {
            deleteOmnichannelSavedFilter(selectedSavedFilter.idFiltroSalvo);
          }
        }}
        onCancel={() => setOmnichannelState('showModalOmnichannelDeleteFilters', false)}
      />
      <ModalSaveFilters
        show={showModalOmnichannelSaveFilters}
        onHide={handleHideModalSavedFilters}
        onChangeName={name => handleChangeNameSavedFilter(name as string)}
        onClickConfirm={handleOmnichannelSaveFilters}
      />
    </Content>
  );
};
